import { CheckCircle2, Download, Loader2, RefreshCw, Upload } from "lucide-react";
import { useState } from "react";
import { AdminState } from "../components/admin/AdminState";
import { PageHeader } from "../components/admin/PageHeader";
import { StatusBadge } from "../components/admin/StatusBadge";
import { adminDownload, adminPost, type ImportBatch, useAdminQuery } from "../components/admin/adminApi";

type ImportsResponse = {
  batches: ImportBatch[];
  ok: true;
};

type HealthResponse = {
  health: {
    database: string;
    failedJobs: number;
    pendingOutbox: number;
    recentErrors: Array<{ createdAt: string; id: string; message: string; severity: string; source: string }>;
    recentWebhooks: Array<{ id: string; receivedAt: string; status: string; topic: string }>;
  };
  ok: true;
};

const sampleCsv =
  "title,body,rating,review_date,reviewer_name,reviewer_email,product_handle,status,picture_urls\nPerfect fit,The quality feels premium,5,24/12/2024,Mariam A.,mariam@example.com,demo-product,ok,https://cdn.example/review.jpg";

export function OperationsPage({ mode }: { mode: string }) {
  const isAi = mode === "AI Tools";
  return isAi ? <AiOperationsPage mode={mode} /> : <ImportExportPage mode={mode} />;
}

function ImportExportPage({ mode }: { mode: string }) {
  const imports = useAdminQuery<ImportsResponse>("/api/admin/imports");
  const [csv, setCsv] = useState(sampleCsv);
  const [format, setFormat] = useState<"generic" | "judgeme">("judgeme");
  const [busy, setBusy] = useState<string>();
  const [notice, setNotice] = useState<{ tone: "critical" | "success"; text: string }>();

  async function previewImport() {
    setBusy("preview");
    setNotice(undefined);
    try {
      const response = await adminPost<{ batch: ImportBatch; ok: true }>("/api/admin/imports/preview", {
        csv,
        filename: format === "judgeme" ? "judgeme-5-star-import.csv" : "admin-import.csv",
        provider: format,
        ratingFilter: format === "judgeme" ? 5 : undefined,
      });
      setNotice({ tone: "success", text: `Previewed ${response.batch.rowCount} rows.` });
      await imports.reload();
    } catch (error) {
      setNotice({ tone: "critical", text: error instanceof Error ? error.message : "Import preview failed." });
    } finally {
      setBusy(undefined);
    }
  }

  async function confirmImport(batchId: string) {
    setBusy(batchId);
    setNotice(undefined);
    try {
      const response = await adminPost<{ importedRows: number; ok: true }>(`/api/admin/imports/${batchId}/confirm`, {});
      setNotice({ tone: "success", text: `Imported ${response.importedRows} reviews.` });
      await imports.reload();
    } catch (error) {
      setNotice({ tone: "critical", text: error instanceof Error ? error.message : "Import confirmation failed." });
    } finally {
      setBusy(undefined);
    }
  }

  async function downloadSettings() {
    await adminDownload("/api/admin/exports/settings.json", "whatstar-settings.json");
  }

  return (
    <div className="wr-page wr-operations-page">
      <PageHeader
        eyebrow="Migration workspace"
        title={mode}
        description="Preview CSV imports, confirm valid rows, export reviews, and download settings snapshots."
        meta={
          <>
            <span>Validation preview</span>
            <span>Duplicate-safe imports</span>
            <span>Settings backup</span>
          </>
        }
        action={
          <div className="wr-header-actions">
            <button className="wr-button wr-button--secondary" type="button" onClick={() => void imports.reload()}>
              <RefreshCw size={15} aria-hidden />
              Refresh
            </button>
            <button className="wr-button" type="button" onClick={() => void adminDownload("/api/admin/exports/reviews.csv", "whatstar-reviews.csv")}>
              <Download size={15} aria-hidden />
              Reviews CSV
            </button>
            <button className="wr-button wr-button--secondary" type="button" onClick={() => void downloadSettings()}>
              <Download size={15} aria-hidden />
              Settings JSON
            </button>
          </div>
        }
      />
      {notice ? <div className={`wr-notice wr-notice--${notice.tone}`}>{notice.text}</div> : null}
      <section className="wr-operations-page__layout">
        <div className="wr-panel wr-import-composer">
          <div className="wr-import-composer__heading">
            <div>
              <h2>Prepare an import</h2>
              <p>Paste rows, select their source format, then validate them before any reviews are created.</p>
            </div>
            <span>Step 1 of 2</span>
          </div>
          <textarea
            className="wr-import-textarea"
            aria-label="Review CSV"
            value={csv}
            onChange={(event) => setCsv(event.target.value)}
          />
          <label className="wr-field wr-import-format-field">
            <span>Import format</span>
            <select value={format} onChange={(event) => setFormat(event.target.value as "generic" | "judgeme")}>
              <option value="judgeme">Judge.me 5-star reviews + media</option>
              <option value="generic">WhatStar CSV</option>
            </select>
          </label>
          <button className="wr-button" type="button" disabled={busy === "preview"} onClick={() => void previewImport()}>
            {busy === "preview" ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <Upload size={15} aria-hidden />}
            Preview import
          </button>
        </div>
        <aside className="wr-import-rail">
          <section className="wr-import-guide" aria-label="Import workflow">
            <strong>Import workflow</strong>
            <ol>
              <li><span>1</span>Preview rows and mapping</li>
              <li><span>2</span>Confirm only valid records</li>
              <li><span>3</span>Review the audit trail</li>
            </ol>
          </section>
          <AdminState
            status={imports.status}
            error={imports.error}
            onRetry={() => void imports.reload()}
            emptyTitle="No imports yet"
            emptyText="Paste a CSV and preview it before confirmation."
          >
            <div className="wr-panel wr-import-history">
              <div className="wr-import-history__heading">
                <div>
                  <h2>Latest imports</h2>
                  <p>Validation and confirmation history for this store.</p>
                </div>
                <span>{imports.data?.batches.length ?? 0}</span>
              </div>
            <ul className="wr-import-list">
              {imports.data?.batches.map((batch) => (
                <li key={batch.id}>
                  <div>
                    <strong>{batch.filename}</strong>
                    <span>
                      {batch.validCount} valid / {batch.errorCount} errors
                    </span>
                  </div>
                  <StatusBadge tone={batch.status === "confirmed" ? "success" : batch.errorCount ? "warning" : "info"}>
                    {batch.status}
                  </StatusBadge>
                  {!batch.confirmedAt ? (
                    <button
                      className="wr-button wr-button--secondary"
                      type="button"
                      disabled={busy === batch.id || batch.validCount === 0}
                      onClick={() => void confirmImport(batch.id)}
                    >
                      {busy === batch.id ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <CheckCircle2 size={15} aria-hidden />}
                      Confirm
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
            </div>
          </AdminState>
        </aside>
      </section>
    </div>
  );
}

function AiOperationsPage({ mode }: { mode: string }) {
  const health = useAdminQuery<HealthResponse>("/api/admin/health");
  return (
    <div className="wr-page wr-operations-page wr-operations-page--ai">
      <PageHeader
        eyebrow="AI and health"
        title={mode}
        description="Inspect enrichment readiness, queue health, recent webhook delivery, and safe diagnostic context."
        meta={
          <>
            <span>Queue health</span>
            <span>Webhook trail</span>
            <span>Enrichment readiness</span>
          </>
        }
        action={
          <button className="wr-button wr-button--secondary" type="button" onClick={() => void health.reload()}>
            <RefreshCw size={15} aria-hidden />
            Refresh
          </button>
        }
      />
      <AdminState
        status={health.status}
        error={health.error}
        onRetry={() => void health.reload()}
        emptyText="Operational health appears after the app is connected to a store."
      >
        <section className="wr-operations-page__health">
          <div className="wr-panel wr-health-panel">
            <div className="wr-health-panel__heading">
              <div>
                <h2>Queue health</h2>
                <p>Live operational safeguards for enrichment and delivery work.</p>
              </div>
              <span>Live checks</span>
            </div>
            <ul className="wr-action-list wr-action-list--health">
              <li>
                <StatusBadge tone="success">{health.data?.health.database}</StatusBadge>
                Database connection
              </li>
              <li>
                <StatusBadge tone={health.data?.health.pendingOutbox ? "warning" : "success"}>
                  {health.data?.health.pendingOutbox ?? 0}
                </StatusBadge>
                Pending outbox jobs
              </li>
              <li>
                <StatusBadge tone={health.data?.health.failedJobs ? "critical" : "success"}>
                  {health.data?.health.failedJobs ?? 0}
                </StatusBadge>
                Failed jobs
              </li>
            </ul>
          </div>
          <div className="wr-panel wr-health-panel">
            <div className="wr-health-panel__heading">
              <div>
                <h2>Recent webhooks</h2>
                <p>The latest Shopify and messaging delivery events received by WhatStar.</p>
              </div>
              <span>{health.data?.health.recentWebhooks.length ?? 0} events</span>
            </div>
            <ul className="wr-action-list wr-action-list--health">
              {health.data?.health.recentWebhooks.length ? (
                health.data.health.recentWebhooks.map((webhook) => (
                  <li key={webhook.id}>
                    <StatusBadge tone="info">{webhook.status}</StatusBadge>
                    {webhook.topic}
                  </li>
                ))
              ) : (
                <li>No webhook events recorded yet.</li>
              )}
            </ul>
          </div>
        </section>
      </AdminState>
    </div>
  );
}
